import React, { useCallback, useContext, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Children } from "../../../../../../../components/miscellianous/children"
import { WarningUpdatedDialog } from "./WarningUpdatedDialog"
import { TabEnum } from "../../../../../../../components/tabs/tabs"
import { BSBuildingContext } from "../../building/components/context/BSBuildingContext"

export const WarningUpdatedDialogContext = React.createContext<WarningUpdatedDialogStore>({} as WarningUpdatedDialogStore)

export function WarningUpdatedDialogContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const navigate = useNavigate()
  const location = useLocation()

  const { selectedBSBuilding } = useContext(BSBuildingContext)

  const [openWarningDialog, setOpenWarningDialog] = useState<boolean>(false)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [selectedTab, setSelectedTab] = useState<TabEnum>(TabEnum.BS_VARIANT_INFO)
  const tmpSelectedTabRef = useRef(TabEnum.BS_VARIANT_INFO)
  const tmpTargetUrlRef = useRef("")
  const modeRef: React.MutableRefObject<"tab" | "url"> = useRef("tab")

  const setUrl: (index: TabEnum, bsBuildingId?: string) => void = useCallback(
    (index, bsBuildingId) => {
      const params = new URLSearchParams(location.search)

      if (
        index === TabEnum.BS_VARIANT_PARCELLE ||
        index === TabEnum.BS_VARIANT_INFO ||
        index === TabEnum.BS_VARIANT_DASHBOARD
      ) {
        params.delete("bsBuildingId")
      } else if (selectedBSBuilding || bsBuildingId) {
        if (selectedBSBuilding) {
          params.set("bsBuildingId", selectedBSBuilding.id)
        } else if (bsBuildingId) {
          params.set("bsBuildingId", bsBuildingId)
        }
      }
      params.delete("tab")
      params.set("tab", index.toString())

      navigate(
        {
          pathname: location.pathname,
          search: params.toString(),
        },
        { replace: true }
      )
    },
    [location.pathname, location.search, navigate, selectedBSBuilding]
  )

  const handleSelectTab: (index: TabEnum, bsBuildingId?: string) => void = useCallback(
    (index, bsBuildingId) => {
      if (index === selectedTab) {
        return
      }
      if (isUpdating) {
        modeRef.current = "tab"
        tmpSelectedTabRef.current = index
        setOpenWarningDialog(true)
      } else {
        setSelectedTab(index)
        setUrl(index, bsBuildingId)
      }
    },
    [isUpdating, selectedTab, setOpenWarningDialog, setSelectedTab, setUrl, tmpSelectedTabRef]
  )

  const handleSelectUrl: (targetUrl: string) => void = useCallback(
    (targetUrl) => {
      if (isUpdating) {
        tmpTargetUrlRef.current = targetUrl
        modeRef.current = "url"
        setOpenWarningDialog(true)
      } else {
        navigate(targetUrl)
      }
    },
    [isUpdating, navigate]
  )

  const warningUpdatedDialogStore: WarningUpdatedDialogStore = useMemo(
    () => ({
      openWarningDialog,
      setOpenWarningDialog,
      isUpdating,
      setIsUpdating,
      selectedTab,
      setSelectedTab,
      tmpSelectedTabRef,
      tmpTargetUrlRef,
      modeRef,
      setUrl,
      handleSelectTab,
      handleSelectUrl,
    }),
    [openWarningDialog, isUpdating, selectedTab, setUrl, handleSelectTab, handleSelectUrl]
  )

  return (
    <WarningUpdatedDialogContext.Provider value={warningUpdatedDialogStore}>
      <WarningUpdatedDialog
        setOpenWarningDialog={setOpenWarningDialog}
        openWarningDialog={openWarningDialog}
        setIsUpdating={setIsUpdating}
        modeRef={modeRef}
        setUrl={setUrl}
        setSelectedTab={setSelectedTab}
        selectedTab={selectedTab}
        tmpSelectedTabRef={tmpSelectedTabRef}
        tmpTargetUrlRef={tmpTargetUrlRef}
      />
      {children}
    </WarningUpdatedDialogContext.Provider>
  )
}

export interface WarningUpdatedDialogStore {
  openWarningDialog: boolean
  setOpenWarningDialog: React.Dispatch<React.SetStateAction<boolean>>
  isUpdating: boolean
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>
  selectedTab: TabEnum
  setSelectedTab: React.Dispatch<React.SetStateAction<TabEnum>>
  tmpSelectedTabRef: React.MutableRefObject<TabEnum>
  tmpTargetUrlRef: React.MutableRefObject<string>
  modeRef: React.MutableRefObject<"tab" | "url">
  setUrl(index: TabEnum, bsBuildingId?: string): void
  handleSelectTab(index: TabEnum, bsBuildingId?: string): void
  handleSelectUrl(targetUrl: string): void
}
